import numeral from 'numeral';
import {
  ColumnFormat,
  MetricsPrefix,
  NumberFormatter,
} from '@/modules/core/app/constants/data.constants';
import { getters } from '@/modules/core/app/helpers/store';
import { DateUtil } from '@/modules/core/app/utils/DateUtil';

class NumeralService {
  /**
   * @param value
   * @param format
   * @param precision
   * @param isCompactNumber
   * @param currencySymbol
   * @param fieldName
   */
  formatValue(
    value,
    format,
    precision = 0,
    isCompactNumber = false,
    currencySymbol = null,
    fieldName = ''
  ) {
    if (value < 1000 || format === ColumnFormat.FORMAT_PERCENT) {
      isCompactNumber = false;
    }

    const precisionFormat =
      value - parseInt(value, 10) > 0 || isCompactNumber
        ? this._getPrecisionFormat(isCompactNumber ? 2 : precision)
        : '';
    const compactNumberFormat = isCompactNumber ? 'a' : '';
    let negativeBrackets = false;

    let numeralFormatCheckNegativeValues;
    const isCalculationField = fieldName.includes(MetricsPrefix.CALCULATION);
    const precisionSuffix = `0,0.${'0'.repeat(precision)}`;
    const isNegative = Math.sign(value) === -1;

    if (isNegative) {
      if (format === ColumnFormat.FORMAT_CURRENCY) {
        negativeBrackets = true;
      }
    }
    if (isCompactNumber && Math.abs(value) >= NumberFormatter.PETA_PREFIX[0]) {
      return this.formatExtendedPrefix(value, format, currencySymbol, negativeBrackets);
    }

    switch (format) {
      case ColumnFormat.FORMAT_DECIMAL:
      case ColumnFormat.FORMAT_CURRENCY:
        numeralFormatCheckNegativeValues = isCalculationField ? precisionSuffix : '0,0.00';
        break;
      case ColumnFormat.FORMAT_PERCENT:
        numeralFormatCheckNegativeValues = precision ? precisionSuffix : `0,0.00`;
        break;
      default:
        numeralFormatCheckNegativeValues = '0,0';
        break;
    }

    let numeralFormat = numeralFormatCheckNegativeValues;
    if (precisionFormat) {
      numeralFormat = `${numeralFormat}.${precisionFormat}`;
    }
    numeralFormat += compactNumberFormat;

    if (format === ColumnFormat.FORMAT_PERCENT) {
      numeralFormat = `${numeralFormat}%`;
      // Since NumeralService converts 2 to 200%.
      value = value ? Number(value) / 100 : value;
    } else if (format === ColumnFormat.FORMAT_TIME) {
      numeralFormat = '00:00:00';
    }

    numeral.nullFormat('-');

    const valueFormmated = this.safeNumeralFormat(value, numeralFormat, format);

    if (format === ColumnFormat.FORMAT_CURRENCY) {
      const currency = currencySymbol ?? getters.session.getUserSettings()?.currencyCode;
      return negativeBrackets
        ? this.negativeValueToBracketedValue(valueFormmated, currency)
        : `${currency}${valueFormmated}`;
    }
    return valueFormmated;
  }

  negativeValueToBracketedValue(formattedValue, currencySymbol) {
    return `(${currencySymbol}${formattedValue.replace('-', '')})`;
  }

  /**
   * * Handle NAN in the number
   * @param value
   * @param numeralFormat
   * @param formatType
   * @returns {*|string}
   */
  safeNumeralFormat(value, numeralFormat, formatType = '') {
    if (formatType === ColumnFormat.FORMAT_CURRENCY && (value === '0' || value === 0)) {
      return 0;
    }
    let formatted = numeral(value).format(numeralFormat).toUpperCase();
    // eslint-disable-next-line tap/no-raw-text-js
    const isInvalid = formatted.includes('NAN');

    if (isInvalid) {
      formatted = Number(value).toFixed(2);
      if (formatType === ColumnFormat.FORMAT_PERCENT) {
        const fallbackValue = Number(value) * 100;
        formatted = `${fallbackValue.toFixed(2)}%`;
      }
    }

    return formatted;
  }

  /**
   * @param value
   * @param {ApiColumn} column
   * @param isCompactNumber
   * @param currencySymbol
   */
  formatColumnValue(value, column, isCompactNumber = false, currencySymbol = null) {
    return this.formatValue(
      value,
      column.format,
      column.precision,
      isCompactNumber,
      currencySymbol
    );
  }

  /**
   * @param value
   * @param format
   * @param currencySymbol
   * @param negativeBrackets
   * @returns {string}
   * @private
   */
  formatExtendedPrefix(value, format, currencySymbol, negativeBrackets) {
    const absValue = Math.abs(value);
    const [divisor, suffix] =
      absValue >= NumberFormatter.EXA_PREFIX[0]
        ? NumberFormatter.EXA_PREFIX
        : NumberFormatter.PETA_PREFIX;
    const formatted = `${(value / divisor).toFixed(2)}${suffix}`;

    if (format === ColumnFormat.FORMAT_CURRENCY) {
      const currency = currencySymbol ?? getters.session.getUserSettings()?.currencyCode;
      return negativeBrackets
        ? this.negativeValueToBracketedValue(formatted, currency)
        : `${currency}${formatted}`;
    }

    return formatted;
  }

  /**
   * @param precision
   * @returns {string|string}
   * @private
   */
  _getPrecisionFormat(precision) {
    let format = '';
    for (let i = 0; i < precision; i++) {
      format += '0';
    }
    return format;
  }

  /**
   * @param value
   * @param comparisonValue
   * @param compareAsValue {boolean}
   * @returns {number}
   */
  calculateDelta(
    value,
    comparisonValue,
    compareAsValue = false,
    format = ColumnFormat.FORMAT_INTEGER
  ) {
    if (format === ColumnFormat.FORMAT_TIME) {
      value = DateUtil.getDurationInSeconds(value);
      comparisonValue = DateUtil.getDurationInSeconds(comparisonValue);
    } else {
      value = Number(value);
      comparisonValue = Number(comparisonValue);
    }

    if (compareAsValue) {
      return value - comparisonValue;
    }

    if (!comparisonValue) return '-';

    return value !== 0 ? ((value - comparisonValue) / Math.abs(comparisonValue)) * 100 : 0;
  }

  /**
   * @param value
   * @returns {number}
   */
  getAbsoluteValue(value) {
    return Math.abs(value) || 0;
  }

  /**
   *
   * @param value
   * @param decimals
   * @returns {number}
   */
  getFixedDecimals(value, decimals = 2) {
    value = value.toString();
    // Don't slice integer number
    if (value.indexOf('.') !== -1) {
      value = value.slice(0, value.indexOf('.') + decimals + 1);
    }
    return Number(value);
  }

  /**
   *
   * @param value
   * @param decimals
   * @returns {number}
   */
  getPercentageOf(firstValue, secondValue, decimals = 2) {
    const percentage = (Number(firstValue) / Number(secondValue)) * 100;
    return this.getFixedDecimals(percentage, decimals);
  }

  /**
   * Remove commas from number
   * @param value
   * @returns {string|*}
   */
  removeCommasFromNumber(value) {
    const cleaned = String(value).replace(/,/g, '');
    return Number(cleaned);
  }

  /**
   * Add zero width to the number
   * @param value
   * @returns {*|string}
   */
  preserveAsString(value) {
    const isNumericLike = (val) =>
      // eslint-disable-next-line tap/no-raw-text-js
      typeof val === 'number' || (typeof val === 'string' && /^[+-]?\d+(\.\d+)?$/.test(val.trim()));

    if (isNumericLike(value)) {
      return `${String(value)}\u200B`; // Use template literal to append zero-width space
    }

    return value;
  }

  /**
   * @param value
   * @returns {*|boolean}
   */
  isNegativeNumberWithCommas(value) {
    const cleaned = String(value).replace(/,/g, '');
    const num = Number(cleaned);
    return Number.isFinite(num) && num < 0;
  }

  /**
   * format negative number -5 to (5)
   * @param val
   * @returns {string}
   */
  formatNegative(val) {
    return `(${this.removeMinus(val)})`;
  }

  /**
   * @param value
   * @returns {string}
   */
  removeMinus(value) {
    // eslint-disable-next-line tap/no-raw-text-js
    if (typeof value !== 'string') value = String(value);
    return value.replace(/^-\s*/, '');
  }
}

export default new NumeralService();
